import { Moment } from "moment";
import { getDate, setTime } from "@miplatform/utils";
import { PrioritySchedulerColumnEvent } from "./PrioritySchedulerBoard.types";
import {
  type TimeWindowRange,
  layoutIntervalFitsBelongingTemplateWindow,
} from "./autoAssignWindow.utils";

const DEFAULT_STACK_GAP_PX = 0;
const MIN_CARD_HEIGHT_PX = 20;
const DEFAULT_EVENT_DURATION_MINUTES = 60;

export interface PrioritySchedulerEventLayout {
  top: number;
  height: number;
}

export interface UseEventLayoutsParams {
  events: PrioritySchedulerColumnEvent[];
  hourHeight: number;
  columnDate: Moment | undefined;
  dayStartTime?: string;
  dayEndTime?: string;
  stackGapPx?: number;
  templateWindowsById?: Record<string, TimeWindowRange[]>;
}

export interface UseEventLayoutsResult {
  layouts: Map<string, PrioritySchedulerEventLayout>;
  maxBottom: number;
}

export interface ComputeEventLayoutsParams extends UseEventLayoutsParams {
  now: Moment;
}

const resolveNonOverlappingTop = (
  desiredTop: number,
  height: number,
  placed: PrioritySchedulerEventLayout[],
  gapPx: number
): number => {
  let top = desiredTop;
  let resolved = false;

  while (!resolved) {
    const bottom = top + height;
    const blocker = findBlocker(placed, top, bottom, gapPx);
    if (!blocker) {
      resolved = true;
    } else {
      top = blocker.top + blocker.height + gapPx;
    }
  }

  return top;
};

const findBlocker = (
  placed: PrioritySchedulerEventLayout[],
  top: number,
  bottom: number,
  gapPx: number
): PrioritySchedulerEventLayout | undefined =>
  placed.find((p) => top < p.top + p.height + gapPx && bottom > p.top - gapPx);

/**
 * Converts a time-of-day string ("HH:mm" or "HH:mm:ss") to a pixel offset
 * from midnight at the given hourHeight.
 */
const timeStringToOffsetPx = (time: string, hourHeight: number): number => {
  const [hours = 0, minutes = 0] = time.split(":").map(Number);
  return (hours + minutes / 60) * hourHeight;
};

/**
 * Converts a Moment to a pixel offset from a reference start Moment.
 */
const momentToOffsetPx = (
  m: Moment,
  startMoment: Moment,
  hourHeight: number
): number => {
  const minutes = m.diff(startMoment, "minutes", true);
  return (minutes / 60) * hourHeight;
};

const durationToHeight = (
  durationMinutes: number | undefined,
  hourHeight: number
): number =>
  durationMinutes ? (durationMinutes / 60) * hourHeight : hourHeight;

type EventCategory = "A" | "B" | "C" | "staged" | "unscheduled";

export const classifyEvent = (
  event: PrioritySchedulerColumnEvent
): EventCategory => {
  if (event.isStaged) return "staged";
  if (event.startDate && event.endDate) return "A";
  if (event.startDate && !event.endDate) return "B";
  if (!event.startDate && !event.endDate && event.scheduledStartDate)
    return "C";
  return "unscheduled";
};

/**
 * Computes absolute `top` / `height` for each event in a priority-scheduler column.
 *
 * Events are classified into categories:
 *  A) Has startDate + endDate  -> fixed position spanning actual work time
 *  B) Has startDate, no endDate -> top at startDate, height grows to current time
 *  C) No startDate/endDate, has scheduledStartDate -> time-anchored, pushed down
 *     by current-time indicator when scheduledStartDate < now
 *  Staged (isStaged) -> placed after assigned events at provisional scheduledStartDate
 *  Unscheduled -> stacked sequentially in remaining space
 *  Window-demoted Category C (calendar has autoAssignWindows only) -> stacked
 *     after dayEndTime when timeline-pushed interval exceeds belonging window
 *
 * Cards that intersect the dayEndTime boundary are pushed below it.
 */
export function computeEventLayouts({
  events,
  hourHeight,
  columnDate,
  dayStartTime,
  dayEndTime,
  stackGapPx = DEFAULT_STACK_GAP_PX,
  templateWindowsById,
  now,
}: Readonly<ComputeEventLayoutsParams>): UseEventLayoutsResult {
  const layouts = new Map<string, PrioritySchedulerEventLayout>();
  const placed: PrioritySchedulerEventLayout[] = [];

  const placeEvent = (id: string, desiredTop: number, height: number) => {
    const top = resolveNonOverlappingTop(
      desiredTop,
      height,
      placed,
      stackGapPx
    );
    const layout: PrioritySchedulerEventLayout = { top, height };
    layouts.set(id, layout);
    placed.push(layout);
    return layout;
  };

  if (!columnDate) {
    let stackCursor = 0;
    events.forEach((event) => {
      const height = durationToHeight(event.expectedDuration, hourHeight);

      let desiredTop = stackCursor;
      if (event.scheduledStartDate) {
        const start = getDate(event.scheduledStartDate);
        const minutesFromMidnight =
          start.hours() * 60 + start.minutes() + start.seconds() / 60;
        desiredTop = Math.max(0, (minutesFromMidnight / 60) * hourHeight);
      }

      const layout = placeEvent(event.id, desiredTop, height);
      stackCursor = Math.max(
        stackCursor,
        layout.top + layout.height + stackGapPx
      );
    });

    let maxBottom = 0;
    placed.forEach(({ top, height }) => {
      maxBottom = Math.max(maxBottom, top + height);
    });
    return { layouts, maxBottom };
  }

  const startMoment = dayStartTime
    ? setTime(columnDate, dayStartTime)
    : getDate(columnDate).startOf("day");

  const nowOffsetPx = momentToOffsetPx(now, startMoment, hourHeight);

  const dayEndOffsetPx = dayEndTime
    ? timeStringToOffsetPx(dayEndTime, hourHeight) -
      timeStringToOffsetPx(dayStartTime ?? "00:00", hourHeight)
    : undefined;

  const hasCalendarTimeWindows =
    templateWindowsById != null && Object.keys(templateWindowsById).length > 0;

  // Phase 1: classify
  const catA: PrioritySchedulerColumnEvent[] = [];
  const catB: PrioritySchedulerColumnEvent[] = [];
  const catC: PrioritySchedulerColumnEvent[] = [];
  const staged: PrioritySchedulerColumnEvent[] = [];
  const unscheduled: PrioritySchedulerColumnEvent[] = [];
  const windowDemoted: PrioritySchedulerColumnEvent[] = [];

  events.forEach((event) => {
    const cat = classifyEvent(event);
    if (cat === "A") catA.push(event);
    else if (cat === "B") catB.push(event);
    else if (cat === "C") catC.push(event);
    else if (cat === "staged") staged.push(event);
    else unscheduled.push(event);
  });

  // Phase 2: Category A - fixed position from startDate to endDate
  catA.forEach((event) => {
    if (!event.startDate || !event.endDate) return;
    const start = getDate(event.startDate);
    const end = getDate(event.endDate);
    const topPx = Math.max(0, momentToOffsetPx(start, startMoment, hourHeight));
    const height = Math.max(
      MIN_CARD_HEIGHT_PX,
      momentToOffsetPx(end, startMoment, hourHeight) - topPx
    );
    placeEvent(event.id, topPx, height);
  });

  // Phase 3: Category B - top at startDate, height grows to now
  catB.forEach((event) => {
    if (!event.startDate) return;
    const start = getDate(event.startDate);
    const topPx = Math.max(0, momentToOffsetPx(start, startMoment, hourHeight));
    const height = Math.max(MIN_CARD_HEIGHT_PX, nowOffsetPx - topPx);
    placeEvent(event.id, topPx, height);
  });

  // Phase 4: Category C - scheduled, pushed by current-time indicator
  const sortedCatC = [...catC].sort((a, b) => {
    const ta = a.scheduledStartDate
      ? getDate(a.scheduledStartDate).valueOf()
      : 0;
    const tb = b.scheduledStartDate
      ? getDate(b.scheduledStartDate).valueOf()
      : 0;
    return ta - tb;
  });

  sortedCatC.forEach((event) => {
    if (!event.scheduledStartDate) return;
    const height = durationToHeight(event.expectedDuration, hourHeight);
    const scheduledStart = getDate(event.scheduledStartDate);
    let desiredTop = Math.max(
      0,
      momentToOffsetPx(scheduledStart, startMoment, hourHeight)
    );

    const isInDayEndWindow = !dayEndOffsetPx || desiredTop < dayEndOffsetPx;
    const wasTimelinePushed = scheduledStart.isBefore(now) && isInDayEndWindow;

    if (wasTimelinePushed) {
      desiredTop = Math.max(desiredTop, nowOffsetPx);
    }

    if (
      wasTimelinePushed &&
      hasCalendarTimeWindows &&
      templateWindowsById &&
      !layoutIntervalFitsBelongingTemplateWindow(
        event.workOrderTemplateId,
        startMoment.clone().add((desiredTop / hourHeight) * 60, "minutes"),
        startMoment
          .clone()
          .add(
            (desiredTop / hourHeight) * 60 +
              (event.expectedDuration ?? DEFAULT_EVENT_DURATION_MINUTES),
            "minutes"
          ),
        scheduledStart,
        templateWindowsById
      )
    ) {
      windowDemoted.push(event);
      return;
    }

    placeEvent(event.id, desiredTop, height);
  });

  // Phase 4b: Staged - after assigned events, at provisional scheduledStartDate
  const sortedStaged = [...staged].sort((a, b) => {
    const ta = a.scheduledStartDate
      ? getDate(a.scheduledStartDate).valueOf()
      : 0;
    const tb = b.scheduledStartDate
      ? getDate(b.scheduledStartDate).valueOf()
      : 0;
    return ta - tb;
  });

  sortedStaged.forEach((event) => {
    if (!event.scheduledStartDate) return;
    const height = durationToHeight(event.expectedDuration, hourHeight);
    const scheduledStart = getDate(event.scheduledStartDate);
    let desiredTop = Math.max(
      0,
      momentToOffsetPx(scheduledStart, startMoment, hourHeight)
    );

    const isInTimeWindow = !dayEndOffsetPx || desiredTop < dayEndOffsetPx;

    if (scheduledStart.isBefore(now) && isInTimeWindow) {
      desiredTop = Math.max(desiredTop, nowOffsetPx);
    }

    placeEvent(event.id, desiredTop, height);
  });

  // Phase 5: Unscheduled - stacked below all placed content
  let stackCursor = 0;
  placed.forEach(({ top, height }) => {
    stackCursor = Math.max(stackCursor, top + height + stackGapPx);
  });

  unscheduled.forEach((event) => {
    const height = durationToHeight(event.expectedDuration, hourHeight);
    const layout = placeEvent(event.id, stackCursor, height);
    stackCursor = Math.max(
      stackCursor,
      layout.top + layout.height + stackGapPx
    );
  });

  // Phase 5b: Window-demoted Category C — stacked after calendar day end
  if (windowDemoted.length > 0) {
    let afterDayEndCursor =
      dayEndOffsetPx ??
      placed.reduce(
        (max, { top, height }) => Math.max(max, top + height + stackGapPx),
        0
      );

    windowDemoted.forEach((event) => {
      const height = durationToHeight(event.expectedDuration, hourHeight);
      const layout = placeEvent(event.id, afterDayEndCursor, height);
      afterDayEndCursor = layout.top + layout.height + stackGapPx;
    });
  }

  // Phase 6: dayEndTime overflow - push cards that intersect the boundary below it
  if (dayEndOffsetPx !== undefined) {
    let overflowCursor = dayEndOffsetPx;

    const sortedByTop = [...layouts.entries()].sort(
      ([, a], [, b]) => a.top - b.top
    );

    sortedByTop.forEach(([id, layout]) => {
      const cardBottom = layout.top + layout.height;
      const intersectsEndOfDay =
        layout.top < dayEndOffsetPx && cardBottom > dayEndOffsetPx;

      if (intersectsEndOfDay || layout.top >= dayEndOffsetPx) {
        const newTop = overflowCursor;
        const updatedLayout = { ...layout, top: newTop };
        layouts.set(id, updatedLayout);

        const placedEntry = placed.find(
          (p) => p.top === layout.top && p.height === layout.height
        );
        if (placedEntry) {
          placedEntry.top = newTop;
        }

        overflowCursor = newTop + layout.height + stackGapPx;
      }
    });
  }

  let maxBottom = 0;
  placed.forEach(({ top, height }) => {
    maxBottom = Math.max(maxBottom, top + height);
  });

  return { layouts, maxBottom };
}
